import React, { useState, useEffect } from 'react';
import { API, useAuth } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Settings, Loader2, CheckCircle, AlertCircle, Zap, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AppSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Hidden apps list
  const hiddenApps = [
    { 
      name: "Ride Pay Extract v2", 
      icon: Zap, 
      path: "/dashboard/ride-pay-extract-v2",
      description: "Advanced payment reconciliation with AI-powered data extraction"
    }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/app-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      setSettings(data.settings);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePaymentExtractor = async (enabled) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/app-settings?payment_extractor_enabled=${enabled}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update settings');
      }

      const data = await response.json();
      setSettings(data.settings);
      toast.success(data.message);
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(error.message || 'Failed to update settings');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleMaintenanceMode = async (enabled) => {
    if (enabled) {
      if (!window.confirm(
        "⚠️ ENABLE MAINTENANCE MODE?\n\n" +
        "This will lock out ALL users (except master admins) from the app.\n\n" +
        "Users will see a maintenance message and cannot access any features.\n\n" +
        "Are you sure?"
      )) {
        return;
      }
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/app-settings?maintenance_mode=${enabled}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update maintenance mode');
      }

      const data = await response.json();
      setSettings(data.settings);
      toast.success(data.message);
      
      if (enabled) {
        toast.info("🔒 Maintenance mode enabled. All users (except master admins) are now locked out.", { duration: 5000 });
      } else {
        toast.success("✅ Maintenance mode disabled. All users can now access the app.", { duration: 5000 });
      }
    } catch (error) {
      console.error('Error updating maintenance mode:', error);
      toast.error(error.message || 'Failed to update maintenance mode');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Settings className="h-8 w-8" />
          App Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage application features and configurations
        </p>
      </div>

      {user?.account_type !== 'master_admin' && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to modify these settings. Only Master Admins can change app settings.
          </AlertDescription>
        </Alert>
      )}

      <Card className="dark:bg-card dark:border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Feature Toggles
          </CardTitle>
          <CardDescription className="dark:text-gray-400">
            Enable or disable application features for all users
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Maintenance Mode Toggle */}
          <div className="flex items-center justify-between p-4 border-2 border-orange-300 dark:border-orange-700 rounded-lg bg-orange-50 dark:bg-orange-900/10">
            <div className="space-y-1">
              <Label className="text-base font-semibold dark:text-white flex items-center gap-2">
                <EyeOff className="h-4 w-4" />
                Maintenance Mode
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Lock out ALL users (except master admins) from the entire app. Users will see a stylish maintenance message.
              </p>
              <div className="flex items-center gap-2 mt-2">
                {settings?.maintenance_mode ? (
                  <span className="text-xs font-medium px-2 py-1 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    🔒 App Locked (Maintenance Active)
                  </span>
                ) : (
                  <span className="text-xs font-medium px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    ✅ App Active (Users Can Access)
                  </span>
                )}
              </div>
            </div>
            <Switch
              checked={settings?.maintenance_mode || false}
              onCheckedChange={handleToggleMaintenanceMode}
              disabled={updating || user?.account_type !== 'master_admin'}
            />
          </div>

          {/* Payment Data Extractor Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg dark:border-border">
            <div className="space-y-1">
              <Label className="text-base font-semibold dark:text-white">
                Payment Data Extractor
              </Label>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Allow users to upload and process payment screenshots. When disabled, users will see a notification that the app is under maintenance.
              </p>
              <div className="flex items-center gap-2 mt-2">
                {settings?.payment_extractor_enabled ? (
                  <span className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    Currently Enabled
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                    <AlertCircle className="h-4 w-4" />
                    Currently Disabled
                  </span>
                )}
              </div>
            </div>
            <Switch
              checked={settings?.payment_extractor_enabled || false}
              onCheckedChange={handleTogglePaymentExtractor}
              disabled={updating || user?.account_type !== 'master_admin'}
              className="data-[state=checked]:bg-green-600"
            />
          </div>

          {updating && (
            <Alert className="bg-primary/10 border-blue-200 dark:bg-primary/20 dark:border-blue-800">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                Updating settings...
              </AlertDescription>
            </Alert>
          )}

          {user?.account_type === 'master_admin' && (
            <Alert className="bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800 dark:text-yellow-200">
                <strong>Note:</strong> When you disable an app, all users will be unable to access it. They will see a message directing them to contact you for the new app link.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Hidden Apps Section */}
      <Card className="dark:bg-card dark:border-border mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <EyeOff className="h-5 w-5" />
            Hidden Apps
          </CardTitle>
          <CardDescription className="dark:text-gray-400">
            Apps that are not displayed in the sidebar but can still be accessed directly
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {hiddenApps.map((app) => {
            const IconComponent = app.icon;
            return (
              <div 
                key={app.path}
                className="flex items-center justify-between p-4 border rounded-lg dark:border-border hover:bg-muted dark:hover:bg-gray-700/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 rounded-lg bg-blue-100 dark:bg-primary/20">
                    <IconComponent className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-base font-semibold dark:text-white cursor-pointer">
                      {app.name}
                    </Label>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {app.description}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate(app.path)}
                  className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Open App
                </Button>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default AppSettings;
